import dayjs, { Dayjs } from 'dayjs'
import React, { useState } from 'react'

import { DropdownOption } from '@/entities/dropdowns/models/DropdownOption'
import { selectAllRunsheetsWithShifts } from '@/entities/schedules'
import { Button } from '@/shared/components'
import { DatePicker } from '@/shared/components/DatePicker/DatePicker'
import { SelectDropdown } from '@/shared/components/SelectDropdown/SelectDropdown'
import { useToastContext } from '@/shared/components/Toast/ToastContext'
import { useAppSelector } from '@/shared/store'
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, FormControl } from '@mui/material'

import { useReplayContext } from './ReplayContext'

type ReplaySettingsDialogProps = {
  open: boolean
  onClose: () => void
}

export const ReplaySettingsDialog: React.FC<ReplaySettingsDialogProps> = ({ open, onClose }) => {
  const { loadReplayData } = useReplayContext()

  const [selectedRun, setSelectedRun] = useState<DropdownOption | null>(null)
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(dayjs())
  const [dateTo, setDateTo] = useState<Dayjs | null>(dayjs())
  const [isLoading, setIsLoading] = useState(false)
  const { setToastContent } = useToastContext()

  const runsheets = useAppSelector(selectAllRunsheetsWithShifts)

  // Build a flat list of all runs from runsheets
  const runOptions: DropdownOption[] = runsheets.flatMap(runsheet => runsheet.shifts.map(shift => ({
    value: shift.runId,
    label: `${runsheet.name} > ${shift.name}`,
    uiColor: shift.uiColor,
  })))

  const handleSubmit = async () => {
    if (!selectedRun || !dateFrom || !dateTo) {
      return
    }

    setIsLoading(true)

    if (dateTo.diff(dateFrom, 'day') > 2) {
      setToastContent({
        open: true,
        severity: 'error',
        title: 'Date Range Too Large',
        content: 'Please select a date range of 2 days or less for viewing historical records.',
      })

      setIsLoading(false)

      return
    }

    try {
      await loadReplayData(
        selectedRun.value,
        selectedRun.label,
        selectedRun.uiColor,
        dateFrom,
        dateTo,
      )
      onClose()
    } catch (error) {
      console.error('Error loading replay data:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={(_ev, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          onClose()
        }
      }}
    >
      <DialogTitle>Historical Trace Settings</DialogTitle>
      <DialogContent>
        <Box sx={{ width: 400, mt: 2 }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <SelectDropdown
              disablePortal={false}
              value={selectedRun}
              disabled={isLoading}
              onChange={(e, selectedOption) => {
                if (!selectedOption) {
                  setSelectedRun(null)

                  return
                }

                const run = runOptions.find(r => r.value === selectedOption.value) || null

                setSelectedRun(run)
              }}
              options={runOptions}
              label="Select Run"
            />
          </FormControl>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <DatePicker
              label="From Date"
              value={dateFrom}
              disabled={isLoading}
              onChange={(newValue) => setDateFrom(newValue)}
              sx={{ flex: 1 }}
            />

            <DatePicker
              label="To Date"
              value={dateTo}
              disabled={isLoading}
              onChange={(newValue) => setDateTo(newValue)}
              maxDate={dateFrom ? dateFrom?.add(2, 'day') : dayjs().add(1, 'day')}
              sx={{ flex: 1 }}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button text onClick={onClose} disabled={isLoading}>Cancel</Button>
        <Button
          primary
          onClick={handleSubmit}
          disabled={isLoading || !selectedRun || !dateFrom || !dateTo}
        >
          {isLoading ? 'Loading...' : 'Load Replay Data'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}